import { axiosInstance } from '@/utils/libs/axios';
import { makeModuleOptions } from '@/utils/libs/moduleUtils';
import { TreeNode } from 'primereact/treenode';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IUserSchema } from './user';
import { z } from 'zod';

export const moduleSchema = z.object({
  id: z.string().optional(),
  module_name: z.string().max(255),
  order: z.number().optional(),
  total_submodule: z.number().optional(),
  total_test_case: z.number().optional(),
  total_test_run_detail: z.number().optional(),
  project_id: z.string().optional(),
  parent_id: z.string().optional().nullable(),
  pic_id: z.string().optional().nullable(),
  is_archive: z.boolean().optional(),
});

export const moveTestCaseSchema = z.object({
  idTestCase: z.string().array(),
  moduleId: z.string(),
});

export type IMoveTestCaseSchema = z.infer<typeof moveTestCaseSchema>;

export type IModuleSchema = z.infer<typeof moduleSchema> & {
  submodule?: IModuleSchema[];
  pic?: IUserSchema;
};

export const ModuleService = {
  useGet: (id: string) =>
    useQuery<IModuleSchema>({
      queryKey: ['module', id],
      queryFn: async () => {
        const res = await axiosInstance.get(`/api/crud/module/${id}`);
        return res.data;
      },
    }),

  useGetAll: ({
    projectId,
    testRunId,
    isArchive = false,
  }: {
    projectId?: string;
    testRunId?: string;
    isArchive?: boolean;
  }) =>
    useQuery<IModuleSchema[]>({
      queryKey: ['all-module', projectId, testRunId, isArchive],
      queryFn: async () => {
        const url = isArchive
          ? `/api/crud/module/all-archive`
          : `/api/crud/module/all`;

        const res = await axiosInstance.get(url, {
          params: {
            filter: {
              project_id: projectId,
              test_run_id: testRunId,
            },
          },
        });

        const content = res.data ?? [];

        return content as IModuleSchema[];
      },
    }),

  useGetByProjectOrByParent: ({
    projectId,
    parentId,
  }: {
    projectId: string;
    parentId?: string;
  }) =>
    useQuery<IModuleSchema[]>({
      queryKey: ['all-module-by-project-or-by-parent', projectId, parentId],
      queryFn: async () => {
        const res = await axiosInstance.get(`/api/crud/module/all`, {
          params: {
            filter: {
              project_id: projectId,
              parent_id: parentId,
            },
            sort: 'order',
            asc: true,
          },
        });
        const content = res.data ?? [];

        return content as IModuleSchema[];
      },
    }),

  useGetModulesOption: (projectId?: string) =>
    useQuery<TreeNode[]>({
      queryKey: ['module-option', projectId],
      queryFn: async () => {
        const res = await axiosInstance.get(`/api/crud/module/all`, {
          params: {
            filter: { project_id: projectId },
            sort: 'order',
            asc: true,
          },
        });

        return makeModuleOptions(res.data ?? []);
      },
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: IModuleSchema) => {
        const res = await axiosInstance.post('/api/crud/module', data);
        return res.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const res = await axiosInstance.delete(`/api/crud/module/${id}`);
        return res.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: IModuleSchema) => {
        const res = await axiosInstance.post(
          `/api/crud/module/${data.id}`,
          data,
        );
        return res.data;
      },
    }),

  updateSync: () => {
    const mutate = (data: IModuleSchema) =>
      axiosInstance.post(`/api/crud/module/${data.id}`, {
        module_name: data.module_name,
        order: data.order,
        parent_id: data.parent_id,
      });

    return { mutate };
  },

  useArchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/module/${id}/archive`,
        );

        return response.data;
      },
    }),

  useUnarchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/module/${id}/unarchive`,
        );
        return response.data;
      },
    }),

  useClone: () =>
    useMutation({
      mutationFn: async ({ id, payload }: { id: string; payload: IModuleSchema }) => {
        const response = await axiosInstance.post(
          `/api/crud/module/${id}/clone`,
          payload,
        );
        return response.data;
      },
    }),

  useMoveTestCase: () =>
    useMutation({
      mutationFn: async ({ payload }: { payload: IMoveTestCaseSchema }) => {
        const response = await axiosInstance.post(
          `/api/api/crud/move-test-case`,
          payload,
        );
        return response.data;
      },
    }),
};
